/**
 * Camera Intelligence — Dynamic PreProduct button + price + subtotal  (v2)
 *
 * GOAL: the four shopper-facing numbers must ALWAYS agree, for every
 * variant (Black/Silver × Body only / Body + Lens) and every plan
 * (Deposit / Pay early):
 *   1. Main price   2. Subtotal   3. Button text   4. "today" subtitle
 *
 * HOW (v2 — race-free):
 *   - Authoritative per-variant base prices come from the embedded product
 *     JSON (`script[type="application/json"]` containing "variants"), which now
 *     includes `price` + `compare_at_price` (store-currency cents).
 *   - We calibrate ONE display-currency rate from a single native DOM price
 *     read (handles Shopify Markets £/€/¥ conversion). rate = displayPrice /
 *     basePrice. Every other number is then derived = base × rate, so we never
 *     depend on Shopify's async re-render timing (the old race) and never read
 *     back a value we ourselves modified (the old pay-early bug).
 *   - Every write targets ALL matching elements (querySelectorAll), so the
 *     desktop column and the mobile sticky bar can never diverge.
 *
 * NOTE: DEPOSIT_PERCENT / PAY_EARLY_PERCENT below must match the actual
 * PreProduct selling-plan charges. If PreProduct charges a flat deposit
 * (e.g. $250) rather than 25%, change DEPOSIT_MODE to 'flat'.
 */
(function () {
  'use strict';

  window.preproductDynamicButtonLoaded = true;

  var CONFIG = {
    DEPOSIT_MODE: 'percent',     // 'percent' | 'flat'
    DEPOSIT_PERCENT: 25,
    DEPOSIT_FLAT: 250,           // used only when DEPOSIT_MODE === 'flat'
    PAY_EARLY_PERCENT: 15,
    FALLBACK_CURRENCY: '£'
  };

  var PLAN_MATCH = {
    deposit: /deposit[- ]based|deposit/i,
    payEarly: /pay early/i
  };

  // ==========================================================================
  // Money parsing / formatting (locale-aware)
  // ==========================================================================

  function parseMoney(text) {
    if (!text) return null;
    var t = String(text).trim();
    var amtMatch = t.match(/[0-9][0-9.,\s ]*/);
    if (!amtMatch) return null;
    var numStr = amtMatch[0].replace(/[\s ]/g, '');
    var commaPos = numStr.lastIndexOf(',');
    var periodPos = numStr.lastIndexOf('.');
    var decimalChar = null;
    if (commaPos > -1 && periodPos > -1) {
      decimalChar = (commaPos > periodPos) ? ',' : '.';
    } else if (commaPos > -1 || periodPos > -1) {
      var sepChar = (commaPos > -1) ? ',' : '.';
      var sepPos = (commaPos > -1) ? commaPos : periodPos;
      var afterSep = numStr.length - sepPos - 1;
      decimalChar = (afterSep >= 1 && afterSep <= 2) ? sepChar : null;
    }
    var normalized;
    if (decimalChar === ',') normalized = numStr.replace(/\./g, '').replace(',', '.');
    else if (decimalChar === '.') normalized = numStr.replace(/,/g, '');
    else normalized = numStr.replace(/[,.]/g, '');
    var amount = parseFloat(normalized);
    if (isNaN(amount)) return null;
    var curMatch = t.match(/[\$£€¥]/);
    return {
      amount: amount,
      currency: curMatch ? curMatch[0] : CONFIG.FALLBACK_CURRENCY,
      decimalChar: decimalChar
    };
  }

  function localeForFormat() {
    var sample = document.querySelector('.sale-price.main-price, .regular-price.main-price, .main-price');
    if (sample) {
      var t = (sample.textContent || '').trim();
      var lc = t.lastIndexOf(','), lp = t.lastIndexOf('.');
      if (lc > -1 && lp > -1) return (lc > lp) ? 'de-DE' : 'en-GB';
      if (lc > -1) { var after = t.length - lc - 1; if (after >= 1 && after <= 2) return 'de-DE'; }
    }
    return 'en-GB';
  }
  function fmtInt(symbol, amount) {
    return symbol + Math.round(amount).toLocaleString(localeForFormat());
  }
  function fmtDecimal(symbol, amount) {
    return symbol + amount.toLocaleString(localeForFormat(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // ==========================================================================
  // Authoritative variant data (embedded product JSON)
  // ==========================================================================

  var basePrice = {};    // variantId(str) -> major-unit store-currency price
  var baseCompare = {};  // variantId(str) -> major-unit compare-at price

  function loadVariantData() {
    var scripts = document.querySelectorAll('script[type="application/json"]');
    for (var i = 0; i < scripts.length; i++) {
      var txt = scripts[i].textContent || '';
      if (txt.indexOf('"variants"') === -1) continue;
      try {
        var data = JSON.parse(txt);
        if (!data || !data.variants || !data.variants.length) continue;
        var found = false;
        data.variants.forEach(function (v) {
          if (!v || v.id == null) return;
          var id = String(v.id);
          if (typeof v.price === 'number') { basePrice[id] = v.price / 100; found = true; }
          if (typeof v.compare_at_price === 'number') baseCompare[id] = v.compare_at_price / 100;
        });
        if (found) return true;
      } catch (e) { /* try next script */ }
    }
    return false;
  }

  // ==========================================================================
  // DOM helpers
  // ==========================================================================

  function currentVariantId() {
    var u = new URL(location.href);
    var id = u.searchParams.get('variant');
    if (id) return id;
    var input = document.querySelector('form[action*="/cart"] input[name="id"], form input[name="id"]');
    if (input && input.value) return input.value;
    // Single-variant fallback
    var keys = Object.keys(basePrice);
    return keys.length === 1 ? keys[0] : null;
  }

  function allPriceEls() {
    var set = [];
    // SCOPE to the MAIN product price only. product-price.liquid also outputs
    // .main-price inside the `other_deals` and `complementary_products` blocks
    // (cross-sells); those are NOT wrapped in [data-render-price], so scoping to
    // it guarantees we never overwrite a cross-sell price with this product's.
    document.querySelectorAll('[data-render-price] .main-price').forEach(function (el) {
      if (set.indexOf(el) === -1) set.push(el);
    });
    if (!set.length) {
      // Fallback only if the render-attr wrapper is absent: first standalone price.
      var first = document.querySelector('.sale-price.main-price, .regular-price.main-price, .main-price');
      if (first) set.push(first);
    }
    return set;
  }

  function readNativeMoney() {
    // A price element we have NOT modified is the native (Shopify-rendered) value.
    var els = allPriceEls();
    for (var i = 0; i < els.length; i++) {
      if (els[i].getAttribute('data-ci-modified') === 'true') continue;
      var m = parseMoney(els[i].textContent);
      if (m && m.amount > 0) return m;
    }
    return null;
  }

  function currentQuantity() {
    var input = document.querySelector('input[name="quantity"], .quantity__input, input.quantity-input');
    if (!input) return 1;
    var n = parseInt(input.value, 10);
    return n > 0 ? n : 1;
  }

  function readSelectedPlan() {
    var radios = document.querySelectorAll('input[name="selling_plan"], input.pp-selling-plans__radio');
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      if (!r.checked) continue;
      var label = '';
      var wrap = r.closest('label');
      if (wrap) label = wrap.textContent || '';
      if (!label && r.id) { var sib = document.querySelector('label[for="' + r.id + '"]'); if (sib) label = sib.textContent || ''; }
      if (!label && r.parentElement) label = r.parentElement.textContent || '';
      if (PLAN_MATCH.payEarly.test(label)) return 'payEarly';
      if (PLAN_MATCH.deposit.test(label)) return 'deposit';
    }
    return null;
  }

  // ==========================================================================
  // Currency-rate calibration + authoritative display price
  // ==========================================================================

  var rate = null;                    // display-currency per store-currency unit
  var curSymbol = CONFIG.FALLBACK_CURRENCY;

  function calibrate() {
    var vid = currentVariantId();
    if (!vid || basePrice[vid] == null) return false;
    var m = readNativeMoney();
    if (!m || m.amount <= 0) return false;
    // Only trust the calibration if the native price plausibly matches this
    // variant (guards against reading a stale price mid-variant-switch).
    if (rate != null) {
      var expected = basePrice[vid] * rate;
      if (expected > 0 && Math.abs(m.amount - expected) / expected > 0.05) {
        // DOM still shows another variant's price — skip this calibration.
        return false;
      }
    }
    rate = m.amount / basePrice[vid];
    curSymbol = m.currency;
    return true;
  }

  function displayPrice(vid) {
    if (basePrice[vid] == null || rate == null) return null;
    return basePrice[vid] * rate;
  }
  function displayCompare(vid) {
    if (baseCompare[vid] == null || rate == null) return null;
    return baseCompare[vid] * rate;
  }

  function depositAmount(fullPrice) {
    return CONFIG.DEPOSIT_MODE === 'flat'
      ? CONFIG.DEPOSIT_FLAT
      : fullPrice * CONFIG.DEPOSIT_PERCENT / 100;
  }

  // ==========================================================================
  // Writers — ALWAYS target every matching element
  // ==========================================================================

  function writeMainPrices(shown, compare) {
    allPriceEls().forEach(function (el) {
      el.textContent = fmtDecimal(curSymbol, shown);
      el.setAttribute('data-ci-modified', 'true');
      // strikethrough: previous sibling <s class="regular-price"> if present
      var wrap = el.parentElement || el;
      var s = wrap.querySelector('s.regular-price') || (wrap.previousElementSibling && wrap.previousElementSibling.matches && wrap.previousElementSibling.matches('s') ? wrap.previousElementSibling : null);
      if (compare && compare > shown) {
        if (s) { s.textContent = fmtDecimal(curSymbol, compare); s.style.display = ''; }
        else {
          var ns = document.createElement('s');
          ns.className = 'regular-price --text-alternative';
          ns.style.marginRight = '10px'; ns.style.opacity = '0.65';
          ns.setAttribute('data-ci-injected', 'true');
          ns.textContent = fmtDecimal(curSymbol, compare);
          el.parentNode.insertBefore(ns, el);
        }
      } else if (s) {
        // hide injected strikethrough; leave native ones alone only if they were native
        if (s.getAttribute('data-ci-injected') === 'true') s.style.display = 'none';
        else s.style.display = 'none';
      }
    });
  }

  function writeSubtotals(total) {
    document.querySelectorAll('[data-qty-subtotal]').forEach(function (el) {
      el.textContent = fmtDecimal(curSymbol, total);
      el.setAttribute('data-ci-subtotal-modified', 'true');
    });
  }

  function findButtons() {
    var list = [];
    // PreProduct injects its deposit button at runtime with this attribute.
    document.querySelectorAll('[data-native-pre-order-btn]').forEach(function (b) { list.push(b); });
    // This section's native ATC button only. (Dropped the broad
    // button--primary selector — the real button is button--secondary, and the
    // broad one risked matching cross-sell / complementary "Add" buttons.)
    document.querySelectorAll('button.featured-product_atc-btn, button.main-product_atc-btn').forEach(function (b) {
      if (list.indexOf(b) === -1) list.push(b);
    });
    return list;
  }

  function setButtonText(button, newText) {
    var walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT, null, false);
    var node;
    while ((node = walker.nextNode())) {
      var t = (node.nodeValue || '').trim();
      if (t && t.length > 3 && /reserve|deposit|pay|save|pre.?order|today/i.test(t)) { node.nodeValue = newText; return true; }
    }
    var span = button.querySelector('span:not([class*="icon"])');
    if (span) { span.textContent = newText; return true; }
    button.textContent = newText;
    return true;
  }

  function writeButtons(label) {
    findButtons().forEach(function (b) { setButtonText(b, label); });
  }

  function rewriteSubtitleToday(deposit) {
    if (!deposit || deposit <= 0) return;
    var newPrefix = fmtInt(curSymbol, deposit);
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null, false);
    var node;
    while ((node = walker.nextNode())) {
      var t = node.nodeValue || '';
      if (!/balance/i.test(t)) continue;
      var re = /[\$£€¥]\s?\d[\d,\.]*\s*today/i;
      if (re.test(t)) { var u = t.replace(re, newPrefix + ' today'); if (u !== t) node.nodeValue = u; }
    }
  }

  function rewriteSubtitleSave(savings) {
    if (!savings || savings <= 0) return;
    var newPrefix = 'Save ' + fmtInt(curSymbol, savings);
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null, false);
    var node;
    while ((node = walker.nextNode())) {
      var t = node.nodeValue || '';
      if (!/lowest\s+price/i.test(t)) continue;
      var re = /save\s*[\$£€¥]\s?\d+(?:,\d{3})*(?:\.\d{1,2})?/i;
      if (re.test(t)) { var u = t.replace(re, newPrefix); if (u !== t) node.nodeValue = u; }
    }
  }

  function updateSavingsBadge(plan, savings) {
    var anchor = document.querySelector('[data-render-price]') || document.querySelector('.featured-product_row--variation_price');
    if (!anchor) return;
    var badge = document.getElementById('ci-savings-badge');
    if (plan === 'payEarly' && savings > 0) {
      if (!badge) {
        badge = document.createElement('div');
        badge.id = 'ci-savings-badge';
        badge.style.cssText = 'margin-top:4px;display:inline-block;padding:4px 10px;background:#2DD4A8;color:#1A1A1A;border-radius:12px;font-size:12px;font-weight:600;letter-spacing:0.3px;';
        anchor.parentElement.insertBefore(badge, anchor.nextSibling);
      }
      badge.textContent = 'Save ' + fmtInt(curSymbol, savings) + ' with Pay Early';
      badge.style.display = 'inline-block';
    } else if (badge) { badge.style.display = 'none'; }
  }

  // ==========================================================================
  // Core reconcile — single source of truth → all four numbers agree
  // ==========================================================================

  var busy = false;

  function reconcile() {
    if (busy) return;
    busy = true;
    try {
      if (!Object.keys(basePrice).length) loadVariantData();
      // Calibrate the display rate from a confidently-native read when possible.
      calibrate();

      var vid = currentVariantId();
      if (!vid) return;

      var full = displayPrice(vid);
      if (full == null) {
        // Last-resort fallback: use the native DOM price directly.
        var m = readNativeMoney();
        if (m && m.amount > 0) { full = m.amount; curSymbol = m.currency; }
      }
      if (full == null || full <= 0) return;

      var compare = displayCompare(vid);
      var plan = readSelectedPlan() || 'deposit';
      var qty = currentQuantity();

      var shown, savings = 0, label, dep;
      if (plan === 'payEarly') {
        shown = Math.round(full * (1 - CONFIG.PAY_EARLY_PERCENT / 100) * 100) / 100;
        savings = full - shown;
        writeMainPrices(shown, full);                       // strike through full price
        writeSubtotals(shown * qty);
        label = 'Pay ' + fmtInt(curSymbol, shown) + ' today · Save ' + fmtInt(curSymbol, savings);
        rewriteSubtitleSave(savings);
      } else {
        shown = full;
        writeMainPrices(shown, (compare && compare > full) ? compare : null);
        writeSubtotals(full * qty);
        dep = depositAmount(full);
        label = 'Reserve with ' + fmtInt(curSymbol, dep) + ' deposit';
        rewriteSubtitleToday(dep);
      }
      writeButtons(label);
      updateSavingsBadge(plan, savings);
    } finally {
      busy = false;
    }
  }

  // Debounced scheduler — coalesces the burst of events a variant switch fires,
  // and re-runs a few times so it settles after Shopify's own re-render.
  var timer = null;
  function schedule(times) {
    times = times || 1;
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      reconcile();
      if (times > 1) schedule(times - 1);
    }, 60);
  }

  // ==========================================================================
  // Event wiring
  // ==========================================================================

  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('input[type="radio"], select, input[name="quantity"], .quantity__input, input.quantity-input')) {
      schedule(2);
    }
  }, true);

  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.pp-selling-plans__radio, .pp-selling-plans__option, label[class*="selling"], variant-pill, .variant-pill, [data-variant-option], .product-variant-options label')) {
      schedule(3);
    }
  }, true);

  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches && e.target.matches('input[name="quantity"], .quantity__input, input.quantity-input')) {
      schedule(1);
    }
  }, true);

  // Variant ?variant= changes via history API
  var lastUrl = location.href;
  function onUrlChange() { if (location.href !== lastUrl) { lastUrl = location.href; schedule(2); } }
  (function () {
    var ps = history.pushState, rs = history.replaceState;
    history.pushState = function () { ps.apply(this, arguments); onUrlChange(); };
    history.replaceState = function () { rs.apply(this, arguments); onUrlChange(); };
    window.addEventListener('popstate', onUrlChange);
  })();

  // Re-sync when Shopify natively re-renders the price (variant switch).
  function observePrice() {
    var el = document.querySelector('[data-render-price]') || document.querySelector('.main-price');
    if (!el) return;
    try {
      var mo = new MutationObserver(function () { if (!busy) schedule(1); });
      mo.observe(el, { childList: true, subtree: true, characterData: true });
    } catch (e) { /* noop */ }
  }

  function start() {
    loadVariantData();
    var attempts = 0;
    var iv = setInterval(function () {
      attempts++;
      reconcile();
      if (attempts === 3) observePrice();
      if (attempts > 30) clearInterval(iv);
    }, 500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
